// ENG-9491 — flush an agent's session transcripts to S3 on the drain path,
// BEFORE the agent's files are torn down, so a host-removal migration doesn't
// lose the conversation that was open at the moment of migration.
//
// WHY THIS EXISTS. Migrating an agent between hosts rebinds control-plane state
// (host_agents, charter/tools, keys) but copies no disk state; the conversation
// lives only in the source host's on-disk transcript. The daily archiver
// (scripts/agt-session-archiver.sh, sibling ENG-9490) would eventually ship it,
// but on a host-REMOVAL migration the source host is torn down before its next
// nightly run — and the session open at migration is by definition newer than
// any sweep has shipped. So the manager flushes it synchronously on drain.
//
// HOW. There is no reusable TypeScript uploader — the archiver is a shell script
// using `aws s3 cp`. Rather than add @aws-sdk/client-s3 to the CLI bundle (it
// ships to every host), this reuses the host's own AWS CLI + IAM the exact same
// way, and keys every object as `${keyPrefix}/${rel}` via the address the
// archiver publishes (readHostArchiveAddress) — never a reconstructed prefix
// (ENG-8438: the enforced key is the full-ARN shape, read it, don't rebuild it).
//
// QUIESCENCE (AC2). The transcript JSONL is written only by the Claude Code
// session, which stopAgentRuntime has already killed at the call site. The one
// external writer that survives a dead session — the host-resident WhatsApp
// inbound API route — writes to ~/.augmented/{codeName}/whatsapp-pending-inbound/,
// a DIFFERENT tree, never the transcript store. So once the runtime is stopped
// the transcript is quiescent and safe to ship.
//
// FAILURE (AC4). The flush is best-effort in that it must never throw and block
// teardown — the agent is gone from this host regardless. But a failure is
// logged LOUDLY and reported in the result; it is never silently treated as a
// success, so a lost session is visible rather than mistaken for "nothing to
// ship".
//
// ---------------------------------------------------------------------------
// ENG-9566 — THE MANIFEST. Shipping the bytes is not enough to restore a
// conversation, because two facts a destination host needs are archived by
// nothing, and both are known ONLY here:
//
//   1. The session pin. `resolveSessionSpawnDecision` resumes a conversation
//      only when a transcript exists for *today's pinned UUID*; that pin lives
//      at ~/.augmented/<codeName>/daily-session.json, which no sweep covers
//      (see readDailySessionPin). Without it a destination mints a fresh UUID
//      and every restored transcript is an orphan — a restore that copies
//      everything and continues nothing.
//
//   2. The key layout. An object's key is `${keyPrefix}/${rel}`, where `rel`
//      begins with the ENCODED project dir — and whether that encodes the
//      codename-keyed or the id-keyed path depends on per-host on-disk state
//      (getProjectDir → resolveRealAgentPath self-detects via lstatSync). Once
//      the source host is torn down that fact is gone, and it is not safely
//      reconstructible centrally: host-archive-address.ts's header sets out at
//      length why a second producer of one address is the wrong answer.
//
// So the flush writes a manifest object recording both, plus the exact keys it
// uploaded. It is written LAST and, on a host with an address and a keyable
// transcript dir, ALWAYS — including when uploads were incomplete, because a
// destination needs to know a partial set exists rather than infer silence.
// Presence therefore means "the flush ran to completion"; the `complete` flag
// inside means "and every file made it". A destination must read both: treating
// presence alone as a green light would resume against a half-restored
// conversation. Files shipped but no manifest is reported as its own status
// ('no-manifest'), never as success — that upload is unrestorable.

import { existsSync, readdirSync } from 'node:fs';
import { homedir } from 'node:os';
import { dirname, join, relative, sep } from 'node:path';
import { spawn } from 'node:child_process';

import { readHostArchiveAddress } from './host-archive-address.js';
import type { HostArchiveAddress } from './tool-call-extractor.js';

// Re-exported so tests (and callers) can type an injected address without
// reaching into tool-call-extractor for the shape.
export type { HostArchiveAddress } from './tool-call-extractor.js';
import { getProjectDir } from './persistent-session.js';
import { readDailySessionPin, sessionTranscriptDir } from './daily-session.js';
import type { DailySessionPinSnapshot } from './daily-session.js';

// Re-exported so a restore (ENG-9567) can type a manifest's pin without
// importing the daily-session module for one interface.
export type { DailySessionPinSnapshot } from './daily-session.js';

/**
 * Base dir every transcript key is relative to. Must match `SESSIONS_DIR` in
 * scripts/agt-session-archiver.sh (`/root/.claude/projects` on a prod host =
 * `~/.claude/projects`), so a drain-flushed object lands under the SAME key the
 * daily sweep would have used — one coherent per-host tree, not a second layout.
 */
export function sessionsBaseDir(): string {
  return join(homedir(), '.claude', 'projects');
}

/** Per-file upload timeout. A hung `aws s3 cp` must never stall the drain, so
 *  every upload is bounded and the subprocess is killed past this. */
const PER_FILE_UPLOAD_TIMEOUT_MS = 30_000;

/** Uploads run concurrently but bounded — a drain has a handful of transcript
 *  files, and each `aws s3 cp` is its own subprocess, so a small pool ships them
 *  without spawning an unbounded fleet of processes. */
const UPLOAD_CONCURRENCY = 4;

export type DrainFlushStatus =
  | 'flushed' // every transcript uploaded AND the manifest written
  | 'no-transcripts' // nothing on disk to ship (benign)
  | 'no-address' // host has no resolved S3 address (LOUD — files NOT shipped)
  | 'no-manifest' // every file shipped but the manifest did not (LOUD — unrestorable)
  | 'partial' // some uploaded, some failed (LOUD)
  | 'failed'; // had files + address, none uploaded (LOUD)

/**
 * What became of the manifest write. Separate from `status` because a partial
 * upload and a missing manifest are independent failures — collapsing them
 * would let one mask the other, and a destination needs to distinguish "some
 * files are missing" from "I cannot tell which files there should be".
 */
export type DrainManifestOutcome =
  | 'written' // in S3; safe for a destination to act on
  | 'unkeyable' // transcript dir is outside baseDir, so no key could be computed
  | 'failed' // the upload itself failed
  | 'skipped'; // never attempted (no transcripts, or no address to write to)

/** Layout the SOURCE host used for this agent's on-disk dir (ADR-0049). */
export type DrainSourceLayout = 'codename-keyed' | 'id-keyed';

export interface DrainSourceLayoutInfo {
  layout: DrainSourceLayout;
  /** The real ~/.augmented/<...> dir the project path resolved through. */
  agentDir: string | null;
}

/** Bumped when the manifest's shape changes in a way a reader must branch on. */
export const DRAIN_MANIFEST_VERSION = 1;

/**
 * Manifest filename. Deliberately NOT `*.jsonl`, so listJsonlFiles can never
 * pick it up as a transcript and a later flush cannot re-ship it as session
 * content. The leading underscore keeps it adjacent to, but sorted apart from,
 * the session files in a bucket listing.
 */
export const DRAIN_MANIFEST_FILENAME = '_augmented-drain-manifest.json';

/**
 * The record a destination host reads to turn a pile of restored bytes back
 * into a resumable conversation. Everything here is knowable only on the
 * source host at drain time — see the ENG-9566 note in the module header.
 */
export interface DrainManifest {
  version: number;
  codeName: string;
  writtenAt: string; // ISO 8601
  /**
   * Which on-disk layout produced the encoded segment inside every key.
   * null only when the source could not determine it — distinct from any
   * particular layout, so a reader never guesses on a failed read.
   */
  sourceLayout: DrainSourceLayout | null;
  sourceAgentDir: string | null;
  sourceTranscriptDir: string;
  sourceBaseDir: string;
  bucket: string;
  keyPrefix: string;
  /** Keys this flush successfully uploaded. */
  keys: string[];
  /** Local files it could NOT ship. Non-empty ⇔ `complete` is false. */
  missing: string[];
  /**
   * Every transcript made it. A destination must NOT claim continuity when this
   * is false — the manifest's presence only proves the flush ran to the end.
   */
  complete: boolean;
  /**
   * The pin that says which restored transcript is today's live conversation.
   * null means the source could NOT read it — as opposed to a snapshot with
   * `present: false`, which means the agent genuinely had no pin. A restore
   * must treat the two differently: the second is a fresh agent, the first is
   * a conversation whose identity was lost.
   */
  sessionPin: DailySessionPinSnapshot | null;
}

export interface DrainFlushResult {
  status: DrainFlushStatus;
  uploaded: number;
  failed: number;
  total: number;
  manifest: DrainManifestOutcome;
  /** Key the manifest was written to, when it was. */
  manifestKey: string | null;
}

export interface UploadArgs {
  file: string;
  bucket: string;
  key: string;
  region: string | null;
}

/** Manifest upload: an in-memory body, not a file on disk. */
export interface UploadManifestArgs {
  body: string;
  bucket: string;
  key: string;
  region: string | null;
}

export interface DrainFlushDeps {
  /** Resolve an agent's real on-disk transcript dir (id-keyed-aware). */
  resolveTranscriptDir: (codeName: string) => string;
  /** Read the resolved S3 address the archiver published (bucket + keyPrefix). */
  readAddress: () => HostArchiveAddress | null;
  /** List the agent's *.jsonl transcript files (absolute paths). */
  listTranscripts: (dir: string) => string[];
  /** Upload one file; resolves ok + a short failure detail. Never rejects. */
  uploadFile: (args: UploadArgs) => Promise<{ ok: boolean; detail: string }>;
  /** Upload the manifest body. Never rejects. */
  uploadManifest: (args: UploadManifestArgs) => Promise<{ ok: boolean; detail: string }>;
  /** Which on-disk layout this host used for the agent (ENG-9566). */
  readSourceLayout: (codeName: string) => DrainSourceLayoutInfo;
  /** Snapshot the agent's daily-session pin (ENG-9566). */
  readSessionPin: (codeName: string) => DailySessionPinSnapshot;
  /** Injected clock, so the manifest's timestamp is deterministic under test. */
  now: () => Date;
  /** Base dir keys are computed relative to (sessionsBaseDir in prod). */
  baseDir: string;
  log: (msg: string) => void;
}

/** Recursively list `*.jsonl` files under `dir` (absolute paths). Empty when the
 *  dir is absent — a torn-down or never-created transcript store is not an error. */
export function listJsonlFiles(dir: string): string[] {
  if (!existsSync(dir)) return [];
  const out: string[] = [];
  const walk = (d: string): void => {
    for (const entry of readdirSync(d, { withFileTypes: true })) {
      const full = join(d, entry.name);
      if (entry.isDirectory()) walk(full);
      else if (entry.isFile() && entry.name.endsWith('.jsonl')) out.push(full);
    }
  };
  try {
    walk(dir);
  } catch {
    // A readdir can race teardown/rotation; return whatever we collected rather
    // than throw. A missed file is a smaller loss than aborting the whole flush.
  }
  return out;
}

/**
 * S3 object key for a transcript file: `${keyPrefix}/${rel}`, where `rel` is the
 * file's path relative to `baseDir`, POSIX-separated. Mirrors the archiver's
 * `key_prefix/${rel}`. Returns null when the file is not under `baseDir` (a key
 * we cannot compute safely — better to skip and log than to place it wrongly).
 */
export function transcriptObjectKey(
  keyPrefix: string,
  baseDir: string,
  file: string,
): string | null {
  const rel = relative(baseDir, file).split(sep).join('/');
  if (!rel || rel.startsWith('../') || rel === '..') return null;
  return `${keyPrefix.replace(/\/+$/, '')}/${rel}`;
}

/**
 * Key for the drain manifest: the agent's own encoded transcript directory,
 * with DRAIN_MANIFEST_FILENAME inside it. Sitting under the same encoded
 * segment as the transcripts it describes is what makes it findable by a
 * destination that lists the prefix but cannot compute the encoding itself.
 *
 * Returns null when the transcript dir is not under `baseDir` — the same
 * condition that makes every one of its files unkeyable.
 */
export function drainManifestObjectKey(
  keyPrefix: string,
  baseDir: string,
  transcriptDir: string,
): string | null {
  return transcriptObjectKey(keyPrefix, baseDir, join(transcriptDir, DRAIN_MANIFEST_FILENAME));
}

/**
 * Flush an agent's transcripts to S3. Pure over its deps (DI) so it is unit
 * testable without a real host, AWS CLI, or filesystem. Never rejects.
 *
 * Async, and uploads run through a small bounded-concurrency pool: this is on
 * the manager's drain path, so a synchronous per-file upload would block the
 * whole poll loop's event loop while an agent migration ships (up to the
 * per-file timeout × N). `await`ing async subprocesses keeps the event loop free
 * for every other agent while these uploads run, and the caller still awaits the
 * whole flush before teardown, so the ship-before-cleanup ordering is preserved.
 */
export async function flushAgentTranscriptsOnDrain(
  codeName: string,
  deps: DrainFlushDeps,
): Promise<DrainFlushResult> {
  const dir = deps.resolveTranscriptDir(codeName);
  const files = deps.listTranscripts(dir);
  if (files.length === 0) {
    // Nothing to restore, so nothing to describe. A destination reading "no
    // manifest" here is reading the truth: this agent had no conversation on
    // this host to carry across.
    return {
      status: 'no-transcripts',
      uploaded: 0,
      failed: 0,
      total: 0,
      manifest: 'skipped',
      manifestKey: null,
    };
  }

  const address = deps.readAddress();
  // readHostArchiveAddress returns null unless BOTH bucket and keyPrefix are
  // present; the extra field guards narrow the nullable types and keep this
  // correct even if that contract ever loosens.
  if (!address || !address.bucket || !address.keyPrefix) {
    deps.log(
      `[drain-flush] NO S3 ADDRESS for '${codeName}' — ${files.length} transcript file(s) will NOT be shipped ` +
        `before teardown; this session may be lost (ENG-9491). The host archive address is unresolved ` +
        `(agt-session-archiver has published none).`,
    );
    // No address means no S3 to write to, so the manifest cannot help here
    // either — the loss is the unshipped files, not the missing description.
    return {
      status: 'no-address',
      uploaded: 0,
      failed: files.length,
      total: files.length,
      manifest: 'skipped',
      manifestKey: null,
    };
  }

  const { bucket, keyPrefix, region } = address;
  let uploaded = 0;
  let failed = 0;
  // Exactly what shipped and exactly what did not — the manifest reports both,
  // so a destination is never left inferring the set from a bucket listing.
  const keys: string[] = [];
  const missing: string[] = [];

  // Bounded-concurrency worker pool over a shared cursor. Single-threaded JS
  // makes the counter mutations after each `await` race-free.
  let cursor = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const i = cursor;
      cursor += 1;
      if (i >= files.length) return;
      const file = files[i]!;
      const key = transcriptObjectKey(keyPrefix, deps.baseDir, file);
      if (!key) {
        failed += 1;
        missing.push(file);
        deps.log(
          `[drain-flush] SKIP '${file}' for '${codeName}' — outside sessions base dir '${deps.baseDir}', ` +
            `cannot key it (ENG-9491)`,
        );
        continue;
      }
      const res = await deps.uploadFile({ file, bucket, key, region });
      if (res.ok) {
        uploaded += 1;
        keys.push(key);
      } else {
        failed += 1;
        missing.push(file);
        deps.log(
          `[drain-flush] UPLOAD FAILED for '${codeName}' '${file}' → s3://${bucket}/${key}: ${res.detail} (ENG-9491)`,
        );
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(UPLOAD_CONCURRENCY, files.length) }, () => worker()),
  );

  const uploadStatus: DrainFlushStatus =
    failed === 0 ? 'flushed' : uploaded === 0 ? 'failed' : 'partial';
  if (uploadStatus !== 'flushed') {
    deps.log(
      `[drain-flush] INCOMPLETE for '${codeName}': ${uploaded}/${files.length} transcript file(s) shipped, ` +
        `${failed} FAILED before teardown — session data may be lost (ENG-9491).`,
    );
  }

  // ENG-9566: the manifest goes LAST, after every upload has settled. Its
  // presence is the signal a destination uses to decide the flush ran to the
  // end, so it must never precede the files it claims to describe. It is
  // written even on a partial flush — `complete: false` tells a destination a
  // truncated set exists, which is strictly more useful than silence.
  const { manifest, manifestKey } = await writeDrainManifest(codeName, deps, {
    bucket,
    keyPrefix,
    region,
    transcriptDir: dir,
    keys,
    missing,
    complete: failed === 0,
  });

  // A fully-shipped flush whose manifest did not land is NOT a success: the
  // bytes are in S3 and nothing can identify or place them. Surfaced as its own
  // status rather than folded into 'partial', which would misreport the files.
  const status: DrainFlushStatus =
    uploadStatus === 'flushed' && manifest !== 'written' ? 'no-manifest' : uploadStatus;

  return { status, uploaded, failed, total: files.length, manifest, manifestKey };
}

/** Build and upload the manifest. Never throws — a failure is a reported
 *  outcome, because this runs on a teardown path that must always proceed. */
async function writeDrainManifest(
  codeName: string,
  deps: DrainFlushDeps,
  ctx: {
    bucket: string;
    keyPrefix: string;
    region: string | null;
    transcriptDir: string;
    keys: string[];
    missing: string[];
    complete: boolean;
  },
): Promise<{ manifest: DrainManifestOutcome; manifestKey: string | null }> {
  const manifestKey = drainManifestObjectKey(ctx.keyPrefix, deps.baseDir, ctx.transcriptDir);
  if (!manifestKey) {
    deps.log(
      `[drain-flush] NO MANIFEST for '${codeName}' — transcript dir '${ctx.transcriptDir}' is outside ` +
        `sessions base dir '${deps.baseDir}', so no manifest key could be computed. Any shipped object is ` +
        `UNRESTORABLE: nothing records the session pin or the source layout (ENG-9566).`,
    );
    return { manifest: 'unkeyable', manifestKey: null };
  }

  // Both reads are defensive: a throw here must degrade the manifest, never
  // abort the flush. Each failure is recorded as null — "unknown", which a
  // reader can distinguish from any real value.
  let layout: DrainSourceLayoutInfo | null = null;
  try {
    layout = deps.readSourceLayout(codeName);
  } catch (err) {
    deps.log(
      `[drain-flush] could not resolve source layout for '${codeName}': ${(err as Error).message} — ` +
        `manifest will record it as unknown (ENG-9566)`,
    );
  }
  let sessionPin: DailySessionPinSnapshot | null = null;
  try {
    sessionPin = deps.readSessionPin(codeName);
  } catch (err) {
    deps.log(
      `[drain-flush] could not read the session pin for '${codeName}': ${(err as Error).message} — ` +
        `a restore will not be able to identify the live conversation (ENG-9566)`,
    );
  }

  const manifestBody: DrainManifest = {
    version: DRAIN_MANIFEST_VERSION,
    codeName,
    writtenAt: deps.now().toISOString(),
    sourceLayout: layout?.layout ?? null,
    sourceAgentDir: layout?.agentDir ?? null,
    sourceTranscriptDir: ctx.transcriptDir,
    sourceBaseDir: deps.baseDir,
    bucket: ctx.bucket,
    keyPrefix: ctx.keyPrefix,
    keys: ctx.keys,
    missing: ctx.missing,
    complete: ctx.complete,
    sessionPin,
  };

  const res = await deps.uploadManifest({
    body: `${JSON.stringify(manifestBody, null, 2)}\n`,
    bucket: ctx.bucket,
    key: manifestKey,
    region: ctx.region,
  });
  if (!res.ok) {
    deps.log(
      `[drain-flush] MANIFEST UPLOAD FAILED for '${codeName}' → s3://${ctx.bucket}/${manifestKey}: ` +
        `${res.detail}. ${ctx.keys.length} shipped object(s) are UNRESTORABLE without it (ENG-9566).`,
    );
    return { manifest: 'failed', manifestKey: null };
  }
  return { manifest: 'written', manifestKey };
}

/**
 * Production wiring: resolve the transcript dir via the id-keyed-aware seam,
 * read the published archive address, and upload each file with the host's AWS
 * CLI (the same tool + IAM path the daily archiver uses). Every `aws s3 cp` is
 * time-bounded so a hung upload cannot wedge the manager poll loop.
 */
export function drainTranscriptFlushDeps(log: (msg: string) => void): DrainFlushDeps {
  return {
    resolveTranscriptDir: (codeName) => sessionTranscriptDir(getProjectDir(codeName)),
    readAddress: () => readHostArchiveAddress(),
    listTranscripts: listJsonlFiles,
    baseDir: sessionsBaseDir(),
    now: () => new Date(),
    // ENG-9566: derive the layout from the seam's OUTPUT rather than re-running
    // its lstat. getProjectDir already resolved the codename symlink (or didn't);
    // if what came back is not under the codename dir, this host is id-keyed.
    // Observing the resolver keeps a second copy of ADR-0049's rule out of the
    // tree — the thing host-archive-address.ts's header warns about.
    readSourceLayout: (codeName) => {
      const agentDir = dirname(getProjectDir(codeName));
      const codeNameDir = join(homedir(), '.augmented', codeName); // agent-dir-allow: layout probe, compares against the resolved seam output (ENG-9566)
      return { layout: agentDir === codeNameDir ? 'codename-keyed' : 'id-keyed', agentDir };
    },
    readSessionPin: (codeName) => readDailySessionPin(codeName),
    uploadFile: ({ file, bucket, key, region }) =>
      runAwsCp(awsCpArgs(file, bucket, key, region), null),
    uploadManifest: ({ body, bucket, key, region }) =>
      // `-` is `aws s3 cp`'s stdin source, so the manifest never touches disk.
      runAwsCp(
        [...awsCpArgs('-', bucket, key, region), '--content-type', 'application/json'],
        body,
      ),
    log,
  };
}

function awsCpArgs(source: string, bucket: string, key: string, region: string | null): string[] {
  const args = ['s3', 'cp', source, `s3://${bucket}/${key}`, '--only-show-errors'];
  if (region) args.push('--region', region);
  return args;
}

/**
 * Run one `aws s3 cp`, optionally feeding it a body on stdin. Time-bounded so a
 * hung upload cannot wedge the manager poll loop, and it RESOLVES on every
 * failure path rather than rejecting — the flush's never-throw contract runs
 * through here.
 */
function runAwsCp(args: string[], body: string | null): Promise<{ ok: boolean; detail: string }> {
  return new Promise((resolve) => {
    const child = spawn('aws', args, {
      stdio: [body === null ? 'ignore' : 'pipe', 'ignore', 'pipe'],
    });
    let stderr = '';
    let settled = false;
    const finish = (r: { ok: boolean; detail: string }): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(r);
    };
    const timer = setTimeout(() => {
      child.kill('SIGKILL');
      finish({ ok: false, detail: `aws s3 cp timed out after ${PER_FILE_UPLOAD_TIMEOUT_MS}ms` });
    }, PER_FILE_UPLOAD_TIMEOUT_MS);
    child.stderr?.on('data', (d) => {
      stderr += String(d);
    });
    // `error` fires when `aws` can't be spawned at all (not on PATH); it must
    // resolve, not reject, so one broken upload never rejects the flush.
    child.on('error', (err) => finish({ ok: false, detail: err.message }));
    child.on('close', (code) =>
      finish(
        code === 0
          ? { ok: true, detail: '' }
          : { ok: false, detail: stderr.trim().slice(0, 500) || `aws exited with code ${code ?? 'null'}` },
      ),
    );
    if (body !== null) {
      // If `aws` exits before reading stdin this write EPIPEs, and an unhandled
      // stream 'error' would take the manager process down. Swallow it here and
      // let the `close`/`error` handlers report the real cause.
      child.stdin?.on('error', () => {});
      child.stdin?.end(body);
    }
  });
}
